import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { TaskBase, TaskQuery } from '../../models/Task/Task';
import { taskApi } from '../../services/taskApi';

export interface IncomingCallsState {
  items: TaskBase[];
  totalItemsCount: number;
  status: Status;
}

export interface IncomingCallsResponse {
  items: TaskBase[];
  totalItemsCount: number;
}

export interface IncomingCallsRequest {
  'Filter.From': string;
  'Filter.To': string;
  PageIndex: number;
  PageSize: number;
  SortKeySelector: string;
  'Filter.ClinicIds': string[];
  'Filter.States': string[];
  'Filter.AttemptStates': string[];
  'Filter.Results'?: string[];
  'Filter.Result'?: string[];
  'Filter.Types'?: string[];
  readonly 'Filter.FormTemplateIds'?: string[];
  readonly 'Filter.FormBatchIds'?: string[];
  readonly 'Filter.DispensarizationBatchIds'?: string[];
  'Filter.TextSearch': string;
  SortOrder: string;
}

const buildIncomingSortQuery = (request: IncomingCallsRequest) =>
  JSON.stringify({
    sortKeySelector:
      request.SortKeySelector === 'begin'
        ? 'callDateTime'
        : request.SortKeySelector,
    sortOrder: request.SortOrder || 'Asc',
  });

const toTaskQuery = (request: IncomingCallsRequest): TaskQuery => ({
  from: request['Filter.From'],
  to: request['Filter.To'],
  clinicIds: request['Filter.ClinicIds'],
  states: request['Filter.States'],
  attemptStates: request['Filter.AttemptStates'],
  result: request['Filter.Result'] ?? request['Filter.Results'],
  types: request['Filter.Types'],
  formTemplateIds: request['Filter.FormTemplateIds'],
  formBatchIds: request['Filter.FormBatchIds'],
  dispensarizationBatchIds: request['Filter.DispensarizationBatchIds'],
  textSearch: request['Filter.TextSearch'],
  pageIndex: request.PageIndex,
  pageSize: request.PageSize,
  sortQuery: buildIncomingSortQuery(request),
});

const initialState: IncomingCallsState = {
  items: [],
  totalItemsCount: 0,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getIncomingCalls = createAsyncThunk(
  'IncomingCalls/getIncomingCalls',
  async (request: IncomingCallsRequest, { rejectWithValue }) => {
    try {
      return await taskApi.getInboundTasks(toTaskQuery(request));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue('Не удалось получить список входящих звонков!');
    }
  },
);

export const getInboundTaskTypes = createAsyncThunk(
  'IncomingCalls/getInboundTaskTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await taskApi.getInboundTaskTypes();
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить типы входящих заданий!',
      );
    }
  },
);

export const getInboundTaskReport = createAsyncThunk(
  'IncomingCalls/getInboundTaskReport',
  async (request: IncomingCallsRequest, { rejectWithValue }) => {
    try {
      return await taskApi.getInboundTaskReport(toTaskQuery(request));
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить отчет по входящим заданиям!',
      );
    }
  },
);

export const incomingCallsSlice = createSlice({
  name: 'incomingCalls',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getIncomingCalls.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getIncomingCalls.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.items = action.payload.items ?? [];
        state.totalItemsCount = action.payload.totalItemsCount;
        return state;
      })
      .addCase(getIncomingCalls.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить звонки';
        return state;
      });
  },
});

export default incomingCallsSlice.reducer;

export const selectIncomingCalls = (state: RootState) =>
  state.incomingCalls.items;

export const totalIncomingCalls = (state: RootState) =>
  state.incomingCalls.totalItemsCount;

export const incomingCallsStatus = (state: RootState) =>
  state.incomingCalls.status;
